import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { DateFormat, formatLocalDate } from '@/common/utils/dateFormats';
import { TYPE } from '@/features/agentic-insights/components/experimental/briefing-3/language';
// `InlineMenu` is the shared, token-injected control — this picker doesn't own it and isn't coupled
// to any one variant's copy of it. The remaining coupling to the default variant's look is `TYPE`
// above, imported from `briefing-3/language` on purpose: the picker has to look like part of the
// surface it sits on, and the default variant is what defines that.
import { InlineMenu } from '@/features/agentic-insights/components/experimental/shared/ui/InlineMenu';
import { DEFAULT_VARIANT_ID } from './defaultVariantId';

/**
 * The variants of the experimental view, newest first within each line.
 *
 * A variant belongs to a LINE and carries a number within it. `briefing` is the line the design
 * feedback has been running on: each one reads the run as an intelligence briefing, and each is
 * frozen when feedback is given on it. `intelligence` reads the same run in the product's own
 * recommendation-feed aesthetic — a different bet on the same data, so it is numbered separately
 * rather than taking the next briefing number.
 *
 * `dated` is the day the variant's reading last changed: for a frozen one, the day feedback was
 * given on it; for the one being worked on, the day it was last touched. Written here rather than
 * derived from git, so the picker states plainly which reading a reviewer is looking at.
 *
 * `isDefault` marks the one `…/experimental` redirects to, derived from `DEFAULT_VARIANT_ID`
 * (`defaultVariantId.ts`) rather than written twice — that module is the one place naming the
 * default, since the nav item's bare route renders it directly too. Exactly one entry matches.
 *
 * `classic` is the pre-experimental reading, folded into the lineup rather than living at its own
 * bare-route address. It fits neither the `briefing` line nor the `intelligence` line — it predates
 * both and was never revised alongside them — so it carries its own line rather than a
 * briefing/intelligence number it was never part of.
 */
const VARIANTS = (
  [
    { id: 'briefing-4', line: 'briefing', number: 4, dated: '2026-09-11' },
    { id: 'briefing-3', line: 'briefing', number: 3, dated: '2026-09-10' },
    { id: 'briefing-2', line: 'briefing', number: 2, dated: '2026-09-09' },
    { id: 'loop-intelligence-1', line: 'intelligence', number: 1, dated: '2026-09-09' },
    { id: 'briefing-1', line: 'briefing', number: 1, dated: '2026-09-08' },
    { id: 'classic', line: 'classic', number: 1, dated: '2026-09-11' },
  ] as const
).map((option) => ({ ...option, isDefault: option.id === DEFAULT_VARIANT_ID }));

type Variant = (typeof VARIANTS)[number];
type Line = Variant['line'];

export type VariantId = Variant['id'];

interface VariantPickerProps {
  /** Which variant is being read. Handed down from the route, since the variant is the route. */
  variant: VariantId;
  org: string;
  loopId: number;
}

/**
 * Each line's variants, newest first.
 */
const LINES: { line: Line; latest: Variant; earlier: Variant[] }[] = Array.from(
  VARIANTS.reduce((byLine, current) => {
    const list = byLine.get(current.line) ?? [];
    list.push(current);
    byLine.set(current.line, list);
    return byLine;
  }, new Map<Line, Variant[]>()),
).map(([line, variants]) => {
  const [latest, ...earlier] = [...variants].sort((a, b) => b.number - a.number) as [
    Variant,
    ...Variant[],
  ];
  return { line, latest, earlier };
});

/**
 * One destination per variant, as a literal the route tree can check — a template string built from
 * the id could not be. A `Record` over `VariantId` rather than an if-chain: adding a variant without
 * adding its entry here is then a type error, not a reader quietly sent to the wrong one.
 */
const VARIANT_PATHS = {
  'briefing-4': '/org/$org/loop/$loopId/agentic-insights/experimental/briefing-4',
  'briefing-3': '/org/$org/loop/$loopId/agentic-insights/experimental/briefing-3',
  'briefing-2': '/org/$org/loop/$loopId/agentic-insights/experimental/briefing-2',
  'loop-intelligence-1': '/org/$org/loop/$loopId/agentic-insights/experimental/loop-intelligence-1',
  'briefing-1': '/org/$org/loop/$loopId/agentic-insights/experimental/briefing-1',
  classic: '/org/$org/loop/$loopId/agentic-insights/experimental/classic',
} as const satisfies Record<VariantId, string>;

/**
 * Navigates to a variant, shared by `VariantPicker`'s own dropdown and `RunsCog`'s folded-in variant
 * rows — one place deciding where each id goes, rather than two copies of the same lookup drifting
 * apart.
 */
export const useOpenVariant = (variant: VariantId, org: string, loopId: number) => {
  const navigate = useNavigate();

  return (id: VariantId) => {
    if (id === variant) return;

    navigate({ to: VARIANT_PATHS[id], params: { org, loopId } });
  };
};

export interface VariantRow {
  id: VariantId;
  label: string;
  detail: string;
  tag?: string;
  tagTone?: 'accent';
  selected: boolean;
}

export interface VariantSection {
  line: Line;
  sectionLabel: string;
  goToLatestHint: string;
  latestId: VariantId;
  rows: VariantRow[];
}

/**
 * The picker's data, grouped by line, independent of how it's rendered — `VariantPicker` feeds it to
 * `InlineMenu` as one dropdown; `RunsCog` (briefing-4 only) renders one row per line instead, each
 * variant an inline numbered chip within it, since a second, independently-toggled popover nested
 * inside the cog's already-open one doesn't render. Both read this so the underlying variant/line/date
 * data is never assembled twice.
 */
export const useVariantSections = (variant: VariantId): VariantSection[] => {
  const { t } = useTranslation();

  const sectionLabel = (line: Line) =>
    line === 'briefing'
      ? t(($) => $.loopAgenticInsights.experimental.variants.sectionBriefing)
      : line === 'intelligence'
        ? t(($) => $.loopAgenticInsights.experimental.variants.sectionIntelligence)
        : t(($) => $.loopAgenticInsights.experimental.variants.sectionClassic);

  const goToLatestHint = t(($) => $.loopAgenticInsights.experimental.variants.goToLatest);
  const latestTag = t(($) => $.loopAgenticInsights.experimental.variants.latestTag);
  const classicDated = t(($) => $.loopAgenticInsights.experimental.variants.classicDated);

  // Classic predates the briefing/intelligence numbering entirely — its `dated` field is the day it
  // was folded into this lineup, not a real "last touched" date for classic itself, so running it
  // through the normal formatter would read as recent work that never happened. A fixed label here,
  // rather than a fake `dated` value in the data, keeps the misleading part out of the model
  // entirely.
  const detailFor = (v: Variant) =>
    v.line === 'classic'
      ? classicDated
      : formatLocalDate(v.dated, DateFormat.MONTH_SHORT_DAY_SHORT_YEAR);

  return LINES.map(({ line, latest, earlier }) => ({
    line,
    sectionLabel: sectionLabel(line),
    goToLatestHint,
    latestId: latest.id,
    rows: [latest, ...earlier].map((v) => ({
      id: v.id,
      label: `#${v.number}`,
      detail: detailFor(v),
      tag: v.id === latest.id ? latestTag : undefined,
      tagTone: v.id === latest.id ? ('accent' as const) : undefined,
      selected: v.id === variant,
    })),
  }));
};

/**
 * Picks which variant of this surface to read, in the surface's own idiom rather than the classic
 * tab's: a labelled phrase you click, like the role and the run beside it. Boxed in a `sui-select` it
 * was the loudest control on a page whose whole argument is that the header carries no toolbar.
 *
 * It exists only for the iteration phase — the variants are alternative designs of the same reading,
 * and feedback is given by opening them one after another and comparing.
 *
 * The menu is grouped by line, each under a clickable heading that jumps straight to that line's
 * latest variant. Every variant in a line is listed under it, newest first, so a reviewer can reach
 * any frozen one without an expand step; the newest carries a "Latest" tag rather than relying on the
 * date alone to say so.
 *
 * Rendered directly in the header of every variant except briefing-4, which folds this same data into
 * `RunsCog`'s own popover instead (`useVariantSections`/`useOpenVariant` above) rather than mounting
 * this component — a second, independently-toggled popover nested inside the cog's already-open one
 * does not render correctly.
 */
export const VariantPicker = ({ variant, org, loopId }: VariantPickerProps) => {
  const { t } = useTranslation();
  const sections = useVariantSections(variant);
  const openVariant = useOpenVariant(variant, org, loopId);

  const selected = VARIANTS.find((option) => option.id === variant);
  if (!selected) return null;

  const name = (option: Variant) =>
    option.line === 'briefing'
      ? t(($) => $.loopAgenticInsights.experimental.variants.briefing, { number: option.number })
      : option.line === 'intelligence'
        ? t(($) => $.loopAgenticInsights.experimental.variants.intelligence, {
            number: option.number,
          })
        : t(($) => $.loopAgenticInsights.experimental.variants.classic);

  const rows = sections.flatMap(({ sectionLabel, goToLatestHint, latestId, rows: sectionRows }) => [
    { kind: 'header' as const, id: latestId, label: sectionLabel, hint: goToLatestHint },
    ...sectionRows.map((row) => ({ ...row, inline: true })),
  ]);

  return (
    <span className={TYPE.sentence} data-testid="agentic-x-variant-picker">
      {/* No field label in front of it, unlike the role and the run beside it: "Current Pass" and
          "Pass 1" already say what they are, and "PASS Current Pass" said it twice. */}
      <InlineMenu
        type={TYPE}
        trigger={name(selected)}
        emphasis="inherit"
        ariaLabel={t(($) => $.loopAgenticInsights.experimental.variants.ariaLabel)}
        testId="agentic-x-variant-menu"
        options={rows}
        onSelect={(id) => openVariant(id as VariantId)}
      />
    </span>
  );
};
